import {
  ORDER_ONLINE_PAY_FILTER_OPTIONS,
  ORDER_PERIOD_FILTER_OPTIONS,
  ORDER_STATUS_FILTER_OPTIONS,
  orderListFiltersAreActive,
} from '../../utils/orderFilters.js';

/**
 * @typedef {import('../../utils/orderFilters.js').OrderListFilters} OrderListFilters
 */

/**
 * @param {{
 *   filters: OrderListFilters,
 *   onChange: (next: OrderListFilters) => void,
 *   statusCounts: Record<string, number>,
 *   paymentMethods: string[],
 *   shownCount: number,
 *   totalCount: number,
 * }} props
 */
export function OrdersFilterBar({
  filters,
  onChange,
  statusCounts,
  paymentMethods,
  shownCount,
  totalCount,
}) {
  const active = orderListFiltersAreActive(filters);

  function patch(partial) {
    onChange({ ...filters, ...partial });
  }

  return (
    <section className="space-y-4 rounded-3xl border border-ink/10 bg-white/70 p-4 shadow-sm dark:border-white/10 dark:bg-night-elevated/70">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-ink/45 dark:text-frost-muted">
            Filtros inteligentes
          </p>
          <p className="text-xs text-ink/60 dark:text-frost-muted">
            Mostrando{' '}
            <span className="font-semibold tabular-nums text-ink dark:text-mist">{shownCount}</span> de{' '}
            <span className="font-semibold tabular-nums text-ink dark:text-mist">{totalCount}</span> pedidos
          </p>
        </div>
        {active ? (
          <button
            type="button"
            onClick={() =>
              onChange({
                status: 'all',
                query: '',
                period: 'all',
                onlinePay: 'all',
                paymentMethod: 'all',
              })
            }
            className="rounded-full border border-ink/15 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-ink shadow-sm transition hover:bg-black/5 dark:border-white/15 dark:bg-white/10 dark:text-mist dark:hover:bg-white/15"
          >
            Limpar filtros
          </button>
        ) : null}
      </div>

      <label className="block">
        <span className="sr-only">Pesquisar pedidos</span>
        <input
          type="search"
          value={filters.query}
          onChange={(e) => patch({ query: e.target.value })}
          placeholder="Buscar por nº do pedido, cliente, e-mail, WhatsApp ou forma de pagamento…"
          className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm text-ink shadow-inner outline-none transition focus:border-accent/50 focus:ring-2 focus:ring-accent/20 dark:border-white/15 dark:bg-night-surface dark:text-mist"
        />
      </label>

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por estado do pedido">
        {ORDER_STATUS_FILTER_OPTIONS.map((f) => {
          const count = statusCounts[f.id] ?? 0;
          const isActive = filters.status === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => patch({ status: f.id })}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                isActive
                  ? 'bg-accent text-white shadow-sm'
                  : 'border border-ink/10 bg-white text-ink/75 hover:border-accent/30 hover:bg-accent/5 dark:border-white/10 dark:bg-white/5 dark:text-mist/80 dark:hover:border-accent/35'
              }`}
            >
              {f.label}
              <span
                className={`tabular-nums text-[10px] font-bold ${
                  isActive ? 'text-white/90' : 'text-ink/45 dark:text-frost-muted'
                }`}
              >
                ({count})
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-xs font-semibold text-ink/70 dark:text-frost-muted">
          Período
          <select
            value={filters.period}
            onChange={(e) => patch({ period: e.target.value })}
            className="mt-1 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-night-surface dark:text-mist"
          >
            {ORDER_PERIOD_FILTER_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-xs font-semibold text-ink/70 dark:text-frost-muted">
          Pagamento online
          <select
            value={filters.onlinePay}
            onChange={(e) => patch({ onlinePay: e.target.value })}
            className="mt-1 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-night-surface dark:text-mist"
          >
            {ORDER_ONLINE_PAY_FILTER_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-xs font-semibold text-ink/70 dark:text-frost-muted">
          Forma de pagamento
          <select
            value={filters.paymentMethod}
            onChange={(e) => patch({ paymentMethod: e.target.value })}
            className="mt-1 w-full rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink dark:border-white/15 dark:bg-night-surface dark:text-mist"
          >
            <option value="all">Todas</option>
            {paymentMethods.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );
}
